import { useEffect, useId, useState } from "react"
import { TypedUseLazyQuery } from "@reduxjs/toolkit/query/react"

import { ApiTypes } from "types"

import WidgetsLayout from "layouts/WidgetsLayout"
import { BarChart } from "components/common/BarChart"
import { Spinner } from "components/common/Spinner"

import { SENSOR_WIDGET_COLORS } from "../sensorWidgetColors"
import {
  Content,
  LegendItem,
  LegendStat,
  SensorHeader,
  SensorLegend,
  SensorName,
  SensorRow,
  SensorTotal,
  StyledInfoIcon,
  WhiteTooltip,
  WidgetLoader,
} from "./styled"

const InfoIcon = ({ tooltipId }: { tooltipId: string }) => (
  <StyledInfoIcon
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    width="20"
    height="20"
    data-tooltip-id={tooltipId}
  >
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
      clipRule="evenodd"
    />
  </StyledInfoIcon>
)

const formatStat = (count: number, total: number) => {
  if (total === 0) return `${count}(0%)`
  const pct = Math.round((count / total) * 100)
  return `${count}(${pct}%)`
}

const initialState = {
  thsConnected: 0,
  thsDisconnected: 0,
  wlsConnected: 0,
  wlsDisconnected: 0,
}

export interface SensorStatusesWidgetData {
  thsConnected: number
  thsDisconnected: number
  wlsConnected: number
  wlsDisconnected: number
}

interface SensorStatusRowProps {
  name: string
  connected: number
  disconnected: number
  colors: { connected: string; disconnected: string }
  tooltip: string
  loaded: boolean
}

const SensorStatusRow = ({ name, connected, disconnected, colors, tooltip, loaded }: SensorStatusRowProps) => {
  const tooltipId = useId()
  const total = connected + disconnected
  const hasNoData = loaded && total === 0
  const showPlaceholderBar = !loaded && total === 0

  const renderLegend = () => {
    if (!loaded || hasNoData) return null

    return (
      <>
        <LegendItem $color={colors.connected}>
          Connected <LegendStat>{formatStat(connected, total)}</LegendStat>
        </LegendItem>
        <LegendItem $color={colors.disconnected}>
          Disconnected <LegendStat>{formatStat(disconnected, total)}</LegendStat>
        </LegendItem>
        <InfoIcon tooltipId={tooltipId} />
        <WhiteTooltip id={tooltipId} place="top">
          {tooltip}
        </WhiteTooltip>
      </>
    )
  }

  return (
    <SensorRow>
      <SensorHeader>
        <SensorName>{name}</SensorName>
        <SensorTotal>{total}</SensorTotal>
      </SensorHeader>
      <BarChart
        height={32}
        borderRadius={10}
        showEmptyState
        emptyMessageFontSize={14}
        items={
          showPlaceholderBar
            ? [{ value: 1, color: "#E2E8F0" }]
            : [
                { value: connected, color: colors.connected },
                { value: disconnected, color: colors.disconnected },
              ]
        }
      />
      <SensorLegend>{renderLegend()}</SensorLegend>
    </SensorRow>
  )
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useLazyQuery?: TypedUseLazyQuery<{ data: ApiTypes.Res.SensorStatusesWidget }, void, any>
  widgetData?: Partial<SensorStatusesWidgetData>
  isFetching?: boolean
  title?: string
}

export const WidgetSensorStatuses = ({
  useLazyQuery,
  widgetData: widgetDataProp,
  isFetching: isFetchingProp = false,
  title = "Sensor Statuses",
}: Props) => {
  const lazyQueryResult = useLazyQuery?.()
  const triggerGetSensorStatuses = lazyQueryResult?.[0]
  const lazyQueryState = lazyQueryResult?.[1]
  const isFetching = lazyQueryState?.isFetching ?? isFetchingProp
  const isSuccess = lazyQueryState?.isSuccess ?? Boolean(widgetDataProp)
  const [widgetData, setWidgetData] = useState(initialState)
  const loaded = isSuccess && !isFetching

  useEffect(() => {
    if (widgetDataProp) {
      setWidgetData({
        thsConnected: widgetDataProp.thsConnected ?? 0,
        thsDisconnected: widgetDataProp.thsDisconnected ?? 0,
        wlsConnected: widgetDataProp.wlsConnected ?? 0,
        wlsDisconnected: widgetDataProp.wlsDisconnected ?? 0,
      })
      return
    }

    if (!triggerGetSensorStatuses) return

    const fetchData = async () => {
      const res = await triggerGetSensorStatuses()
      if (res?.data?.data) {
        const d = res.data.data
        setWidgetData({
          thsConnected: d.ths_connected ?? 0,
          thsDisconnected: d.ths_disconnected ?? 0,
          wlsConnected: d.wls_connected ?? 0,
          wlsDisconnected: d.wls_disconnected ?? 0,
        })
      }
    }

    fetchData()
  }, [triggerGetSensorStatuses, widgetDataProp])

  return (
    <WidgetsLayout title={title}>
      <Content $disabled={isFetching}>
        <SensorStatusRow
          name="THS"
          connected={widgetData.thsConnected}
          disconnected={widgetData.thsDisconnected}
          colors={SENSOR_WIDGET_COLORS.ths}
          loaded={loaded}
          tooltip="Current number of Temperature and Humidity sensors connected to or disconnected from thermostats."
        />
        <SensorStatusRow
          name="WLS"
          connected={widgetData.wlsConnected}
          disconnected={widgetData.wlsDisconnected}
          colors={SENSOR_WIDGET_COLORS.wls}
          loaded={loaded}
          tooltip="Current number of Water Leakage sensors connected to or disconnected from thermostats."
        />
        {isFetching && (
          <WidgetLoader>
            <Spinner />
          </WidgetLoader>
        )}
      </Content>
    </WidgetsLayout>
  )
}
